/**
 * @file legacy-v1.ts
 * @description Документ оформления v1 (ступени) и его подъём до v2 (числа) —
 * phase19.16, партия SB120 «свобода выбора», этап 2 «оформление сайта
 * числами» (site-builder.md SB120; проект данных
 * docs/phases/phase19.16-materials/db-design-sb120-stage2.md §1–§2, проект UI
 * uiux-sb120-stage2.md §5–§6).
 *
 * Принцип (uiux-sb120-fields §6): после перевода на сайте не сдвигается ни
 * один пиксель. Каждое число здесь — то, во что ступень v1 превращалась в CSS
 * (css.ts и SiteBackdrop.tsx до этапа 2); золотой тест legacy-v1.test.ts
 * сверяет CSS старого и поднятого документа по всем 23 328 сочетаниям.
 *
 * Таблица перевода (§1):
 *   type.headingScale     s / m / l → 0,875 / 1 / 1,125 (нет ключа — 1 умолчанием);
 *   type.eyebrowTracking  normal / wide / ultra → 0 / 0,08 / 0,2 em;
 *   shapes.buttonShape    → shapes.buttonRadius: square 0 · rounded 10 · pill "pill";
 *   shapes.radius         none 0/0 · soft 8/6 · large 20/12 → { card, field };
 *   shapes.cards          shadow → рамка 0, тень 50 · border → рамка 1, тень 0 · flat → 0/0;
 *   backdrop.haze         true → { strength 18 } · false → { strength 0 };
 *   backdrop.images[]     corner tl/tr/bl/br → x/y 0/0 · 100/0 · 0/100 · 100/100;
 *                         size s → 220 + планшет 160, телефон 100 · m → 320 ·
 *                         l → 380 + 260 / 160 (у m доли совпадают сами);
 *   frame.shadow          true → 50 · false → 0;
 *   spacing.density       compact / normal / spacious → 0,8 / 1 / 1,25;
 *   каталог card.look own radius — только «карточки» (0/8/20), cards — как выше.
 *
 * Функции тотальные и идемпотентные: строка или булево → число; значение уже
 * v2 — не трогается; неизвестное значение остаётся как есть (его отвергнет
 * схема, и `readSiteTheme` заменит часть умолчанием с записью в `replaced`;
 * запись PATCH ответит 400). Вход не меняется — работа на копии.
 *
 * Перечисления v1 переехали сюда из schema.ts; прежние имена
 * (`SITE_HEADING_SCALES`, `SITE_SPACING_DENSITIES`, …) оставлены как
 * @deprecated — ими пользуются подсказки-ступени кабинета до его перевода.
 *
 * Файл — лист графа импортов (только repair.ts): его читают schema.ts,
 * legacy-steps.ts, catalog-options.ts и page-background.ts.
 */

import { cloneJson } from "./repair.js";

// ── Перечисления v1 ──

/** @deprecated SB120 этап 2: размер заголовков — число `type.headingScale`. Ступени v1 — подсказки. */
export const SITE_HEADING_SCALES = ["s", "m", "l"] as const;
/** @deprecated SB120 этап 2: размер заголовков — число. */
export type SiteHeadingScale = (typeof SITE_HEADING_SCALES)[number];

/** Разрядка надписей капсом v1. */
export const SITE_EYEBROW_TRACKINGS_V1 = ["normal", "wide", "ultra"] as const;
export type SiteEyebrowTrackingV1 = (typeof SITE_EYEBROW_TRACKINGS_V1)[number];

/** Форма кнопок v1 (`shapes.buttonShape`). */
export const SITE_BUTTON_SHAPES_V1 = ["square", "rounded", "pill"] as const;
export type SiteButtonShapeV1 = (typeof SITE_BUTTON_SHAPES_V1)[number];

/** Скругление карточек и полей v1 (`shapes.radius`, каталог `card.look.radius`). */
export const SITE_RADIUS_STEPS_V1 = ["none", "soft", "large"] as const;
export type SiteRadiusStepV1 = (typeof SITE_RADIUS_STEPS_V1)[number];

/** Стиль карточек v1 (`shapes.cards`, каталог `card.look.cards`). */
export const SITE_CARD_STYLES_V1 = ["shadow", "border", "flat"] as const;
export type SiteCardStyleV1 = (typeof SITE_CARD_STYLES_V1)[number];

/** Угол рисунка фона v1. */
export const SITE_BACKDROP_CORNERS_V1 = ["tl", "tr", "bl", "br"] as const;
export type SiteBackdropCornerV1 = (typeof SITE_BACKDROP_CORNERS_V1)[number];

/** Размер рисунка фона v1. */
export const SITE_BACKDROP_SIZES_V1 = ["s", "m", "l"] as const;
export type SiteBackdropSizeV1 = (typeof SITE_BACKDROP_SIZES_V1)[number];

/** @deprecated SB120 этап 2: плотность — число `spacing.density`. Ступени v1 — подсказки и перевод. */
export const SITE_SPACING_DENSITIES = ["compact", "normal", "spacious"] as const;
/** Плотность отступов v1 строкой (v2-секции и старые документы). */
export type SiteSpacingDensityV1 = (typeof SITE_SPACING_DENSITIES)[number];

/** Плотность на входе функций перевода: число v2 или строка v1. */
export type SiteDensityInput = number | SiteSpacingDensityV1;

/** @deprecated SB120 этап 2: ширина рамки — любое число 960–1920. Прежние значения — подсказки. */
export const SITE_FRAME_MAX_WIDTHS = [1280, 1440, 1536, 1600] as const;

// ── Таблицы перевода (источник — css.ts и SiteBackdrop.tsx до этапа 2) ──

/** Множитель заголовков (css.ts `HEADING_SCALE_FACTORS`: 0.875 / 1 / 1.125). */
export const HEADING_SCALE_V1: Readonly<Record<SiteHeadingScale, number>> = { s: 0.875, m: 1, l: 1.125 };

/** Разрядка, em (css.ts `TRACKING`: 0 / 0.08em / 0.2em). */
export const EYEBROW_TRACKING_V1: Readonly<Record<SiteEyebrowTrackingV1, number>> = {
  normal: 0,
  wide: 0.08,
  ultra: 0.2,
};

/** Скругление кнопок (css.ts `BUTTON_RADIUS`: 0 / 10px / 9999px → «Пилюля»). */
export const BUTTON_RADIUS_V1: Readonly<Record<SiteButtonShapeV1, number | "pill">> = {
  square: 0,
  rounded: 10,
  pill: "pill",
};

/** Скругление карточек и полей, px (css.ts `RADIUS`: 0/0 · 8px/6px · 20px/12px). */
export const RADIUS_V1: Readonly<Record<SiteRadiusStepV1, { readonly card: number; readonly field: number }>> = {
  none: { card: 0, field: 0 },
  soft: { card: 8, field: 6 },
  large: { card: 20, field: 12 },
};

/** Вид карточек v2: рамка (толщина, цвет — нет ключа = линия сайта) и тень %. */
export interface SiteCardsLookV2 {
  border: { width: number };
  shadow: number;
}

/**
 * Стиль карточек → рамка и тень (css.ts `cardShadow`/`cardBorder`: тень —
 * `SITE_CARD_SHADOW` = 50 %, рамка — `1px solid var(--site-line)`).
 */
export const CARDS_V1: Readonly<Record<SiteCardStyleV1, Readonly<SiteCardsLookV2>>> = {
  shadow: { border: { width: 0 }, shadow: 50 },
  border: { border: { width: 1 }, shadow: 0 },
  flat: { border: { width: 0 }, shadow: 0 },
};

/** Дымка → сила свечения, % (derive.ts `SITE_HAZE_ALPHA` 0,18). */
export const HAZE_STRENGTH_V1 = { on: 18, off: 0 } as const;

/** Тень рамки → % (50 — прежняя `FRAME_SHADOW`). */
export const FRAME_SHADOW_V1 = { on: 50, off: 0 } as const;

/** Угол → место в % области рисунков (SiteBackdrop.tsx `CORNER_CLASS`). */
export const BACKDROP_CORNER_V1: Readonly<Record<SiteBackdropCornerV1, { readonly x: number; readonly y: number }>> = {
  tl: { x: 0, y: 0 },
  tr: { x: 100, y: 0 },
  bl: { x: 0, y: 100 },
  br: { x: 100, y: 100 },
};

/**
 * Размер → ширина px (SiteBackdrop.tsx `SIZE_CLASS`: s 100/160/220 · m
 * 140/220/320 · l 160/260/380). У m планшет и телефон совпадают с долями
 * (`SITE_BACKDROP_SIZE_SHARE`) — ключей нет; у s и l — своё число.
 */
export const BACKDROP_SIZE_V1: Readonly<
  Record<SiteBackdropSizeV1, { readonly size: number; readonly sizeTablet?: number; readonly sizePhone?: number }>
> = {
  s: { size: 220, sizeTablet: 160, sizePhone: 100 },
  m: { size: 320 },
  l: { size: 380, sizeTablet: 260, sizePhone: 160 },
};

/** Плотность → множитель (css.ts `SITE_SPACE_SCALE`: 0.8 / 1 / 1.25). */
export const DENSITY_V1: Readonly<Record<SiteSpacingDensityV1, number>> = {
  compact: 0.8,
  normal: 1,
  spacious: 1.25,
};

/**
 * Множитель плотности: число v2 — как есть; строка v1 — по таблице; что-то
 * иное (битые данные) — 1 («Обычно»): расчёт пикселей не должен падать.
 */
export function densityFactor(density: SiteDensityInput | undefined): number {
  if (typeof density === "number") return Number.isFinite(density) ? density : DENSITY_V1.normal;
  if (typeof density === "string" && hasKey(DENSITY_V1, density)) return DENSITY_V1[density];
  return DENSITY_V1.normal;
}

// ── Подъём ──

/** Итог подъёма: документ v2 (копия) и пути, которые переведены (для журнала моста записи). */
export interface SiteThemeLiftResult {
  doc: unknown;
  lifted: string[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasKey = <K extends string>(table: Readonly<Record<K, unknown>>, value: unknown): value is K =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(table, value);

/** Строка v1 в `obj[key]` → значение таблицы (копия); иное не трогается. */
function liftEnum<K extends string>(
  obj: Record<string, unknown>,
  key: string,
  table: Readonly<Record<K, unknown>>,
  path: string,
  lifted: string[],
): void {
  const value = obj[key];
  if (!hasKey(table, value)) return;
  obj[key] = cloneJson(table[value]);
  lifted.push(path);
}

/** Булево v1 в `obj[key]` → число. */
function liftBool(
  obj: Record<string, unknown>,
  key: string,
  table: { readonly on: number; readonly off: number },
  path: string,
  lifted: string[],
): void {
  const value = obj[key];
  if (typeof value !== "boolean") return;
  obj[key] = value ? table.on : table.off;
  lifted.push(path);
}

/** «Форма кнопок» → «Скругление кнопок»: ключ переименован (db-design-sb120-stage2 §1). */
function liftButtonShape(shapes: Record<string, unknown>, lifted: string[]): void {
  if (!("buttonShape" in shapes)) return;
  const shape = shapes["buttonShape"];
  if (shapes["buttonRadius"] === undefined) {
    // Неизвестная форма остаётся: схема отвергнет лишний ключ, часть заменится умолчанием с записью.
    if (!hasKey(BUTTON_RADIUS_V1, shape)) return;
    shapes["buttonRadius"] = BUTTON_RADIUS_V1[shape];
  }
  // buttonRadius уже есть (смесь от частичной правки) — он главнее, старый ключ снимается.
  delete shapes["buttonShape"];
  lifted.push("shapes.buttonShape");
}

/** Рисунки фона на месте: угол → x/y, размер → px (с планшетом и телефоном у s и l). */
function liftImagesInPlace(list: unknown, path: string, lifted: string[]): void {
  if (!Array.isArray(list)) return;
  list.forEach((item: unknown, index) => {
    if (!isPlainObject(item)) return;
    const corner = item["corner"];
    if (hasKey(BACKDROP_CORNER_V1, corner)) {
      const point = BACKDROP_CORNER_V1[corner];
      if (item["x"] === undefined) item["x"] = point.x;
      if (item["y"] === undefined) item["y"] = point.y;
      delete item["corner"];
      lifted.push(`${path}.${index}.corner`);
    }
    const size = item["size"];
    if (hasKey(BACKDROP_SIZE_V1, size)) {
      const px = BACKDROP_SIZE_V1[size];
      item["size"] = px.size;
      if (px.sizeTablet !== undefined && item["sizeTablet"] === undefined) item["sizeTablet"] = px.sizeTablet;
      if (px.sizePhone !== undefined && item["sizePhone"] === undefined) item["sizePhone"] = px.sizePhone;
      lifted.push(`${path}.${index}.size`);
    }
  });
}

/**
 * Рисунки фона v1 → v2 (тема `backdrop.images`, фон страницы
 * `SitePage.background.decor`, db-design-sb120-stage2 §2). Вход не меняется.
 */
export function liftBackdropImagesV1(list: unknown, path = "backdrop.images"): { images: unknown; lifted: string[] } {
  const lifted: string[] = [];
  const images = cloneJson(list);
  liftImagesInPlace(images, path, lifted);
  return { images, lifted };
}

/**
 * Вид карточки товара v1 → v2 (`catalogOptions.card.look`): `radius`
 * ступенью → px карточки (только card — css.ts `productCardCssVars` брал
 * `RADIUS[…].card`), `cards` ступенью → рамка и тень. Режим (`mode`) не
 * проверяется: частичная правка старой вкладки может прийти без него.
 */
export function liftCatalogCardLookV1(look: unknown, path = "card.look"): { look: unknown; lifted: string[] } {
  const lifted: string[] = [];
  const copy = cloneJson(look);
  if (isPlainObject(copy)) {
    const radius = copy["radius"];
    if (hasKey(RADIUS_V1, radius)) {
      copy["radius"] = RADIUS_V1[radius].card;
      lifted.push(`${path}.radius`);
    }
    liftEnum(copy, "cards", CARDS_V1, `${path}.cards`, lifted);
  }
  return { look: copy, lifted };
}

/**
 * Документ оформления v1 → v2 (запись `SITE_THEME_UPGRADES[1]`, первая строка
 * `fillSiteThemeDefaults`; мост записи `patchTheme` пишет `lifted` в журнал
 * `theme_write_legacy_shape`). Документ уже v2 возвращается копией с
 * пустым `lifted`.
 */
export function liftThemeV1(input: unknown): SiteThemeLiftResult {
  const lifted: string[] = [];
  const doc = cloneJson(input);
  if (!isPlainObject(doc)) return { doc, lifted };

  const type = doc["type"];
  if (isPlainObject(type)) {
    liftEnum(type, "headingScale", HEADING_SCALE_V1, "type.headingScale", lifted);
    liftEnum(type, "eyebrowTracking", EYEBROW_TRACKING_V1, "type.eyebrowTracking", lifted);
  }

  const shapes = doc["shapes"];
  if (isPlainObject(shapes)) {
    liftButtonShape(shapes, lifted);
    liftEnum(shapes, "radius", RADIUS_V1, "shapes.radius", lifted);
    liftEnum(shapes, "cards", CARDS_V1, "shapes.cards", lifted);
  }

  const backdrop = doc["backdrop"];
  if (isPlainObject(backdrop)) {
    const haze = backdrop["haze"];
    if (typeof haze === "boolean") {
      backdrop["haze"] = { strength: haze ? HAZE_STRENGTH_V1.on : HAZE_STRENGTH_V1.off };
      lifted.push("backdrop.haze");
    }
    liftImagesInPlace(backdrop["images"], "backdrop.images", lifted);
  }

  const frame = doc["frame"];
  if (isPlainObject(frame)) liftBool(frame, "shadow", FRAME_SHADOW_V1, "frame.shadow", lifted);

  const spacing = doc["spacing"];
  if (isPlainObject(spacing)) liftEnum(spacing, "density", DENSITY_V1, "spacing.density", lifted);

  return { doc, lifted };
}
